/**
 * Multi-currency display for THEG MARKET PLACE.
 * Catalog unit prices are stored in USD; NGN tiers (when present) are converted via demo FX.
 *
 * Demo FX rates — units of each currency per 1 USD (not live market rates):
 *   USD 1.00
 *   NGN 1600   (Nigeria default)
 *   GBP 0.79   (British pounds — user said "bounds")
 *   CNY 7.25   (Chinese yuan)
 */
import type { CurrencyCode } from '../types';

const STORAGE_KEY = 'forgelink_currency';
const DEFAULT_CODE: CurrencyCode = 'NGN';

export const FX_PER_USD: Readonly<Record<CurrencyCode, number>> = Object.freeze({
  USD: 1,
  NGN: 1600,
  GBP: 0.79,
  CNY: 7.25,
});

/** @deprecated Prefer FX_PER_USD.NGN — kept for existing imports. */
export const USD_TO_NGN = FX_PER_USD.NGN;

type CurrencyMeta = {
  code: CurrencyCode;
  label: string;
  locale: string;
  fractionDigits: number;
};

export const CURRENCIES: Readonly<Record<CurrencyCode, CurrencyMeta>> = Object.freeze({
  NGN: {
    code: 'NGN',
    label: '₦ NGN',
    locale: 'en-NG',
    fractionDigits: 0,
  },
  USD: {
    code: 'USD',
    label: '$ USD',
    locale: 'en-US',
    fractionDigits: 2,
  },
  GBP: {
    code: 'GBP',
    label: '£ GBP',
    locale: 'en-GB',
    fractionDigits: 2,
  },
  CNY: {
    code: 'CNY',
    label: '¥ CNY',
    locale: 'zh-CN',
    fractionDigits: 2,
  },
});

export const CURRENCY_CODES = Object.freeze(Object.keys(CURRENCIES) as CurrencyCode[]);

function normalizeCode(code: string | null | undefined): CurrencyCode {
  const upper = String(code || '').toUpperCase();
  return upper in CURRENCIES ? (upper as CurrencyCode) : DEFAULT_CODE;
}

export function getCurrency(): CurrencyCode {
  try {
    return normalizeCode(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_CODE;
  }
}

export function setCurrency(code: string): CurrencyCode {
  const next = normalizeCode(code);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function convertFromUsd(usd: number, toCode: string = getCurrency()): number {
  const rate = FX_PER_USD[normalizeCode(toCode)] ?? 1;
  return (Number(usd) || 0) * rate;
}

export function convertFromNgn(ngn: number, toCode: string = getCurrency()): number {
  const usd = (Number(ngn) || 0) / FX_PER_USD.NGN;
  return convertFromUsd(usd, toCode);
}

export type FormatMoneyOpts = {
  from?: 'usd' | 'ngn';
  currency?: string;
};

/**
 * Format an amount in the selected (or overridden) display currency.
 */
export function formatMoney(n: number, opts: FormatMoneyOpts = {}): string {
  const from = opts.from || 'usd';
  const code = normalizeCode(opts.currency || getCurrency());
  const meta = CURRENCIES[code];
  const amount = from === 'ngn' ? convertFromNgn(n, code) : convertFromUsd(n, code);
  const rounded =
    meta.fractionDigits === 0 ? Math.round(amount) : Math.round(amount * 100) / 100;
  return new Intl.NumberFormat(meta.locale, {
    style: 'currency',
    currency: code,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: meta.fractionDigits,
    maximumFractionDigits: meta.fractionDigits,
  }).format(rounded);
}

/** Format an amount that is already denominated in NGN. */
export function formatMoneyNgn(ngn: number): string {
  return formatMoney(ngn, { from: 'ngn' });
}
